import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as SchemaIssue from "effect/SchemaIssue";
import * as SchemaTransformation from "effect/SchemaTransformation";
import * as Struct from "effect/Struct";
import { ProviderOptionSelections } from "./model.ts";
import { RepositoryIdentity } from "./environment.ts";
import {
  ApprovalRequestId,
  CheckpointRef,
  CommandId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
} from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import { ToolLifecycleItemType } from "./providerRuntime.ts";

export const ORCHESTRATION_WS_METHODS = {
  dispatchCommand: "orchestration.dispatchCommand",
  listImportableProviderSessions: "orchestration.listImportableProviderSessions",
  listImportableProviderProjects: "orchestration.listImportableProviderProjects",
  importProviderSession: "orchestration.importProviderSession",
  getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
  getTurnDiff: "orchestration.getTurnDiff",
  getFullThreadDiff: "orchestration.getFullThreadDiff",
  getThreadTimelineBefore: "orchestration.getThreadTimelineBefore",
  subscribeShell: "orchestration.subscribeShell",
  subscribeThread: "orchestration.subscribeThread",
} as const;

export const ProviderApprovalPolicy = Schema.Literals([
  "untrusted",
  "on-failure",
  "on-request",
  "never",
]);
export type ProviderApprovalPolicy = typeof ProviderApprovalPolicy.Type;
export const ProviderSandboxMode = Schema.Literals([
  "read-only",
  "workspace-write",
  "danger-full-access",
]);
export type ProviderSandboxMode = typeof ProviderSandboxMode.Type;

/**
 * `ModelSelection` — selection of a model on a configured provider instance.
 *
 * The routing key is `instanceId` (a user-defined slug identifying one
 * configured provider instance). Drivers, credentials, working-directory
 * bindings, and any other per-instance state are recovered from the
 * runtime registry via the instance id.
 *
 * Wire legacy: persisted selections produced before the driver/instance
 * split carried a `provider: <driver-id>` field instead. The schema absorbs
 * that shape via a pre-decoding transform — `{provider, model}` is promoted
 * to `{instanceId: defaultInstanceIdForDriver(provider), model}`. No
 * post-decode compatibility code lives in the runtime; the transform is the
 * only compat surface.
 */
const ModelSelectionWire = Schema.Struct({
  instanceId: ProviderInstanceId,
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(ProviderOptionSelections),
});

// Source shape for persisted legacy payloads. Fields are typed as
// `Schema.Unknown` so malformed drafts still make it into the transform and
// fail validation through the target schema (with proper error messages)
// rather than at the source-struct layer where the error is less actionable.
const ModelSelectionSource = Schema.Struct({
  provider: Schema.optional(Schema.Unknown),
  instanceId: Schema.optional(Schema.Unknown),
  model: Schema.Unknown,
  options: Schema.optional(Schema.Unknown),
});

export const ModelSelection = ModelSelectionSource.pipe(
  Schema.decodeTo(
    ModelSelectionWire,
    SchemaTransformation.transformOrFail({
      decode: (raw) => {
        // Resolve the routing key: prefer an explicit `instanceId`; fall
        // back to promoting the legacy `provider` slug (the canonical
        // `defaultInstanceIdForDriver` mapping) so persisted rollout-era
        // payloads decode without data loss. The target schema brands the
        // string as `ProviderInstanceId`.
        const instanceIdSource =
          raw.instanceId !== undefined
            ? raw.instanceId
            : typeof raw.provider === "string"
              ? raw.provider
              : undefined;
        const base: Record<string, unknown> = {
          instanceId: instanceIdSource,
          model: raw.model,
        };
        if (raw.options !== undefined) base.options = raw.options;
        return Effect.succeed(base as typeof ModelSelectionWire.Encoded);
      },
      encode: (value) => {
        const base: Record<string, unknown> = {
          model: value.model,
          instanceId: value.instanceId,
        };
        if (value.options !== undefined) base.options = value.options;
        return Effect.succeed(base as typeof ModelSelectionSource.Encoded);
      },
    }),
  ),
);
export type ModelSelection = typeof ModelSelection.Type;

export const RuntimeMode = Schema.Literals([
  "approval-required",
  "auto-accept-edits",
  "auto",
  "full-access",
]);
export type RuntimeMode = typeof RuntimeMode.Type;
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";
export const ProviderInteractionMode = Schema.Literals(["default", "plan"]);
export type ProviderInteractionMode = typeof ProviderInteractionMode.Type;
export const DEFAULT_PROVIDER_INTERACTION_MODE: ProviderInteractionMode = "default";
export const ProviderRequestKind = Schema.Literals(["command", "file-read", "file-change"]);
export type ProviderRequestKind = typeof ProviderRequestKind.Type;
export const AssistantDeliveryMode = Schema.Literals(["buffered", "streaming"]);
export type AssistantDeliveryMode = typeof AssistantDeliveryMode.Type;
export const ProviderApprovalDecision = Schema.Literals([
  "accept",
  "acceptForSession",
  "decline",
  "cancel",
]);
export type ProviderApprovalDecision = typeof ProviderApprovalDecision.Type;
export const ProviderUserInputAnswers = Schema.Record(Schema.String, Schema.Unknown);
export type ProviderUserInputAnswers = typeof ProviderUserInputAnswers.Type;

export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 8;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS = 14_000_000;
const CHAT_ATTACHMENT_ID_MAX_CHARS = 128;
const CHAT_ATTACHMENT_URI_MAX_CHARS = 8_192;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(/^[a-z0-9_-]+$/i),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

export const LocalFileChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  uri: TrimmedNonEmptyString.check(
    Schema.isMaxLength(CHAT_ATTACHMENT_URI_MAX_CHARS),
    Schema.isPattern(/^file:/i),
  ),
});
export type LocalFileChatImageAttachment = typeof LocalFileChatImageAttachment.Type;

const UploadChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  dataUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS),
  ),
});
export type UploadChatImageAttachment = typeof UploadChatImageAttachment.Type;

export const ChatAttachment = Schema.Union([ChatImageAttachment, LocalFileChatImageAttachment]);
export type ChatAttachment = typeof ChatAttachment.Type;
const UploadChatAttachment = Schema.Union([
  UploadChatImageAttachment,
  LocalFileChatImageAttachment,
]);
export type UploadChatAttachment = typeof UploadChatAttachment.Type;

export const ProjectScriptIcon = Schema.Literals([
  "play",
  "test",
  "lint",
  "configure",
  "build",
  "debug",
]);
export type ProjectScriptIcon = typeof ProjectScriptIcon.Type;

export const ProjectScript = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  icon: ProjectScriptIcon,
  runOnWorktreeCreate: Schema.Boolean,
  /**
   * URL to open in the in-app browser preview when this script runs (or
   * when the user explicitly requests a preview). Optional; only honored on
   * the desktop build.
   */
  previewUrl: Schema.optional(TrimmedNonEmptyString),
  /**
   * When true, automatically open the preview panel pointed at `previewUrl`
   * the moment this script starts. Ignored without `previewUrl` or on web.
   */
  autoOpenPreview: Schema.optional(Schema.Boolean),
});
export type ProjectScript = typeof ProjectScript.Type;

export const OrchestrationProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const OrchestrationProposedPlanId = TrimmedNonEmptyString;
export type OrchestrationProposedPlanId = typeof OrchestrationProposedPlanId.Type;

export const OrchestrationProposedPlan = Schema.Struct({
  id: OrchestrationProposedPlanId,
  turnId: Schema.NullOr(TurnId),
  planMarkdown: TrimmedNonEmptyString,
  implementedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  implementationThreadId: Schema.NullOr(ThreadId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProposedPlan = typeof OrchestrationProposedPlan.Type;

const SourceProposedPlanReference = Schema.Struct({
  threadId: ThreadId,
  planId: OrchestrationProposedPlanId,
});

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  providerInstanceId: Schema.optional(ProviderInstanceId),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  activeTurnId: Schema.NullOr(TurnId),
  activeSubmissionId: Schema.optional(Schema.NullOr(MessageId)),
  activeUserMessageId: Schema.optional(Schema.NullOr(MessageId)),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

export const OrchestrationThreadBindingState = Schema.Literals([
  "unbound",
  "target_locked",
  "connecting",
  "bound",
  "deleting",
]);
export type OrchestrationThreadBindingState = typeof OrchestrationThreadBindingState.Type;

export const OrchestrationCheckpointFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: TrimmedNonEmptyString,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type OrchestrationCheckpointFile = typeof OrchestrationCheckpointFile.Type;

export const OrchestrationCheckpointStatus = Schema.Literals(["ready", "missing", "error"]);
export type OrchestrationCheckpointStatus = typeof OrchestrationCheckpointStatus.Type;

export const OrchestrationCheckpointSummary = Schema.Struct({
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type OrchestrationCheckpointSummary = typeof OrchestrationCheckpointSummary.Type;

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  payload: Schema.Unknown,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  turnId: TurnId,
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

export const OrchestrationQueuedTurn = Schema.Struct({
  messageId: MessageId,
  text: Schema.String,
  attachments: Schema.Array(ChatAttachment),
  modelSelection: ModelSelection,
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  status: Schema.Literals(["queued", "dispatching", "steering"]),
  createdAt: IsoDateTime,
});
export type OrchestrationQueuedTurn = typeof OrchestrationQueuedTurn.Type;

export const OrchestrationThread = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  settledOverride: Schema.NullOr(Schema.Literals(["settled", "active"])).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  settledAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  // Snooze is an overlay on the active lifecycle, not a fourth destination:
  // a snoozed thread stays "active" in the model and is only suppressed from
  // the inbox until snoozedUntil passes (or the thread raises its hand).
  // Optional so payloads from pre-snooze servers still decode.
  snoozedUntil: Schema.optional(Schema.NullOr(IsoDateTime)),
  snoozedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  deletedAt: Schema.NullOr(IsoDateTime),
  messages: Schema.Array(OrchestrationMessage),
  queuedTurns: Schema.optional(Schema.Array(OrchestrationQueuedTurn)),
  proposedPlans: Schema.Array(OrchestrationProposedPlan).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  activities: Schema.Array(OrchestrationThreadActivity),
  checkpoints: Schema.Array(OrchestrationCheckpointSummary),
  session: Schema.NullOr(OrchestrationSession),
  bindingState: OrchestrationThreadBindingState,
});
export type OrchestrationThread = typeof OrchestrationThread.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationProjectShell = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationThreadShell = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  settledOverride: Schema.NullOr(Schema.Literals(["settled", "active"])).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  settledAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  snoozedUntil: Schema.optional(Schema.NullOr(IsoDateTime)),
  snoozedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  session: Schema.NullOr(OrchestrationSession),
  bindingState: OrchestrationThreadBindingState,
  latestUserMessageAt: Schema.NullOr(IsoDateTime),
  hasPendingApprovals: Schema.Boolean,
  hasPendingUserInput: Schema.Boolean,
  hasActionableProposedPlan: Schema.Boolean,
});
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

export const OrchestrationShellSnapshot = Schema.Struct({
  /**
   * Process-local projection generation. A different epoch is authoritative and
   * invalidates all shell deltas held by the client.
   */
  epoch: TrimmedNonEmptyString,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const OrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    epoch: TrimmedNonEmptyString,
    project: OrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    epoch: TrimmedNonEmptyString,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    epoch: TrimmedNonEmptyString,
    thread: OrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    epoch: TrimmedNonEmptyString,
    threadId: ThreadId,
  }),
]);
export type OrchestrationShellStreamEvent = typeof OrchestrationShellStreamEvent.Type;

export const OrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationShellSnapshot,
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationShellStreamItem = typeof OrchestrationShellStreamItem.Type;

/** Shell subscription is always snapshot-first and has no resume cursor. */
export const OrchestrationSubscribeShellInput = Schema.Struct({});
export type OrchestrationSubscribeShellInput = typeof OrchestrationSubscribeShellInput.Type;

export const OrchestrationTimelineEpoch = TrimmedNonEmptyString;
export type OrchestrationTimelineEpoch = typeof OrchestrationTimelineEpoch.Type;

export const OrchestrationTimelineCursor = TrimmedNonEmptyString;
export type OrchestrationTimelineCursor = typeof OrchestrationTimelineCursor.Type;

export const OrchestrationTimelinePartId = TrimmedNonEmptyString;
export type OrchestrationTimelinePartId = typeof OrchestrationTimelinePartId.Type;

export const BoundedToolPreview = Schema.Struct({
  text: Schema.String,
  truncated: Schema.Boolean,
});
export type BoundedToolPreview = typeof BoundedToolPreview.Type;

const OrchestrationTimelinePartBase = {
  id: OrchestrationTimelinePartId,
  messageId: MessageId,
  turnId: TurnId,
  createdAt: IsoDateTime,
  completedAt: Schema.optional(IsoDateTime),
};

export const OrchestrationTimelineMessagePart = Schema.Union([
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("text"),
    role: OrchestrationMessageRole,
    text: Schema.String,
    streaming: Schema.Boolean,
  }),
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("reasoning"),
    text: Schema.String,
    streaming: Schema.Boolean,
  }),
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("tool"),
    itemType: Schema.optional(ToolLifecycleItemType),
    toolCallId: TrimmedNonEmptyString,
    name: TrimmedNonEmptyString,
    title: Schema.optional(TrimmedNonEmptyString),
    command: Schema.optional(TrimmedNonEmptyString),
    status: Schema.Literals(["pending", "running", "completed", "error"]),
    inputPreview: Schema.optional(BoundedToolPreview),
    outputPreview: Schema.optional(BoundedToolPreview),
    errorPreview: Schema.optional(BoundedToolPreview),
  }),
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("attachment"),
    role: OrchestrationMessageRole,
    name: Schema.optional(TrimmedNonEmptyString),
    mime: Schema.String,
    url: Schema.String,
    unavailableReason: Schema.optional(Schema.Literal("provider-local-file")),
  }),
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("subtask-request"),
    prompt: Schema.String,
    description: TrimmedNonEmptyString,
    agent: TrimmedNonEmptyString,
    command: Schema.optional(TrimmedNonEmptyString),
    model: Schema.optional(
      Schema.Struct({
        providerID: TrimmedNonEmptyString,
        modelID: TrimmedNonEmptyString,
      }),
    ),
  }),
  Schema.Struct({
    ...OrchestrationTimelinePartBase,
    kind: Schema.Literal("agent-reference"),
    name: TrimmedNonEmptyString,
    sourceValue: Schema.optional(TrimmedNonEmptyString),
  }),
]);
export type OrchestrationTimelineMessagePart = typeof OrchestrationTimelineMessagePart.Type;

export const OrchestrationTimelineItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("message-part"),
    part: OrchestrationTimelineMessagePart,
  }),
  Schema.Struct({
    kind: Schema.Literal("message"),
    message: OrchestrationMessage,
  }),
  Schema.Struct({
    kind: Schema.Literal("activity"),
    activity: OrchestrationThreadActivity,
  }),
  Schema.Struct({
    kind: Schema.Literal("proposed-plan"),
    proposedPlan: OrchestrationProposedPlan,
  }),
]);
export type OrchestrationTimelineItem = typeof OrchestrationTimelineItem.Type;

export const OrchestrationTimelineItemRef = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("message-part"), id: OrchestrationTimelinePartId }),
  Schema.Struct({ kind: Schema.Literal("message"), id: MessageId }),
  Schema.Struct({ kind: Schema.Literal("activity"), id: EventId }),
  Schema.Struct({ kind: Schema.Literal("proposed-plan"), id: OrchestrationProposedPlanId }),
]);
export type OrchestrationTimelineItemRef = typeof OrchestrationTimelineItemRef.Type;

/**
 * Thin thread projection accompanying a timeline window. Conversation bodies
 * live only in `OrchestrationTimelineItem`. Checkpoint summaries are a
 * non-persistent projection rebuilt from official Native turn boundaries and
 * deterministic Git refs.
 */
export const OrchestrationThreadTimelineProjection = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime),
  settledOverride: Schema.NullOr(Schema.Literals(["settled", "active"])),
  settledAt: Schema.NullOr(IsoDateTime),
  snoozedUntil: Schema.NullOr(IsoDateTime),
  snoozedAt: Schema.NullOr(IsoDateTime),
  deletedAt: Schema.NullOr(IsoDateTime),
  queuedTurns: Schema.Array(OrchestrationQueuedTurn),
  checkpoints: Schema.optionalKey(Schema.Array(OrchestrationCheckpointSummary)),
  session: Schema.NullOr(OrchestrationSession),
  bindingState: OrchestrationThreadBindingState,
});
export type OrchestrationThreadTimelineProjection =
  typeof OrchestrationThreadTimelineProjection.Type;

export const OrchestrationThreadTimelineWindow = Schema.Struct({
  epoch: OrchestrationTimelineEpoch,
  threadId: ThreadId,
  beforeCursor: Schema.NullOr(OrchestrationTimelineCursor),
  hasOlder: Schema.Boolean,
  projection: OrchestrationThreadTimelineProjection,
  items: Schema.Array(OrchestrationTimelineItem),
});
export type OrchestrationThreadTimelineWindow = typeof OrchestrationThreadTimelineWindow.Type;

export const OrchestrationThreadTimelineDelta = Schema.Struct({
  epoch: OrchestrationTimelineEpoch,
  threadId: ThreadId,
  beforeCursor: Schema.optionalKey(Schema.NullOr(OrchestrationTimelineCursor)),
  hasOlder: Schema.optionalKey(Schema.Boolean),
  projection: Schema.optionalKey(OrchestrationThreadTimelineProjection),
  upsertedItems: Schema.Array(OrchestrationTimelineItem),
  removedItems: Schema.Array(OrchestrationTimelineItemRef),
});
export type OrchestrationThreadTimelineDelta = typeof OrchestrationThreadTimelineDelta.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
  tailLimit: Schema.optionalKey(NonNegativeInt),
  /**
   * Opts into provider working-status items. Servers only emit this transient
   * variant when the client explicitly requests it after capability discovery.
   */
  includeWorkingStatus: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

export const OrchestrationGetThreadTimelineBeforeInput = Schema.Struct({
  threadId: ThreadId,
  epoch: OrchestrationTimelineEpoch,
  beforeCursor: OrchestrationTimelineCursor,
  limit: Schema.optionalKey(NonNegativeInt),
});
export type OrchestrationGetThreadTimelineBeforeInput =
  typeof OrchestrationGetThreadTimelineBeforeInput.Type;

export const OrchestrationGetThreadTimelineBeforeResult = OrchestrationThreadTimelineWindow;
export type OrchestrationGetThreadTimelineBeforeResult =
  typeof OrchestrationGetThreadTimelineBeforeResult.Type;

/**
 * Storage-only shape retained for existing non-conversation cache adapters.
 * It is not used by any HTTP or WebSocket endpoint in this data generation.
 */
export const OrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: OrchestrationThread,
});
export type OrchestrationThreadDetailSnapshot = typeof OrchestrationThreadDetailSnapshot.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  createWorkspaceRootIfMissing: Schema.optional(Schema.Boolean),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  createdAt: IsoDateTime,
});

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
});

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
  force: Schema.optional(Schema.Boolean),
});

const ThreadCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.create"),
  commandId: CommandId,
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
  /** Only send true to servers advertising capabilities.threadDetach. */
  preserveProviderSession: Schema.optional(Schema.Boolean),
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.settle"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnsettleCommand = Schema.Struct({
  type: Schema.Literal("thread.unsettle"),
  commandId: CommandId,
  threadId: ThreadId,
  // Commands only carry "user": activity un-settles are decided server-side
  // (the decider emits thread.unsettled(reason: "activity") events directly,
  // never through this command), so a client cannot forge the neutral reset.
  reason: Schema.Literal("user"),
});

const ThreadSnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.snooze"),
  commandId: CommandId,
  threadId: ThreadId,
  // The wake time. Event-based wake conditions (PR merged, review posted)
  // will arrive as an optional condition field alongside this; time-based
  // snooze is just the first kind of condition.
  snoozedUntil: IsoDateTime,
});

const ThreadUnsnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.unsnooze"),
  commandId: CommandId,
  threadId: ThreadId,
  // Commands only carry "user": activity wakes are decided server-side (the
  // decider emits thread.unsnoozed(reason: "activity") directly), and timer
  // wakes need no event at all — clients derive visibility from snoozedUntil,
  // so a passed wake time simply stops classifying as snoozed.
  reason: Schema.Literal("user"),
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  expectedBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
});

const ThreadRuntimeModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.runtime-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  createdAt: IsoDateTime,
});

const ThreadInteractionModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.interaction-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});

const ThreadTurnBootstrapCreateThread = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

const ThreadTurnBootstrapPrepareWorktree = Schema.Struct({
  projectCwd: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  branch: Schema.optional(TrimmedNonEmptyString),
  startFromOrigin: Schema.optional(Schema.Boolean),
});

const ThreadTurnBootstrap = Schema.Struct({
  createThread: Schema.optional(ThreadTurnBootstrapCreateThread),
  prepareWorktree: Schema.optional(ThreadTurnBootstrapPrepareWorktree),
  runSetupScript: Schema.optional(Schema.Boolean),
});

export type ThreadTurnBootstrap = typeof ThreadTurnBootstrap.Type;

export const ThreadTurnEnqueueCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.enqueue"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
  }),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  bootstrap: Schema.optional(ThreadTurnBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

const ClientThreadTurnEnqueueCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.enqueue"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(UploadChatAttachment),
  }),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  bootstrap: Schema.optional(ThreadTurnBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

const EditableQueuedTurnAttachment = Schema.Union([ChatAttachment, UploadChatAttachment]);

const ClientThreadQueuedTurnUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.queued-turn.update"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  message: Schema.Struct({
    text: Schema.String,
    attachments: Schema.Array(EditableQueuedTurnAttachment),
  }),
  createdAt: IsoDateTime,
});

const ThreadQueuedTurnUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.queued-turn.update"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  message: Schema.Struct({
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
  }),
  createdAt: IsoDateTime,
});

const ThreadQueuedTurnDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.queued-turn.delete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  createdAt: IsoDateTime,
});

const ThreadQueuedTurnSteerCommand = Schema.Struct({
  type: Schema.Literal("thread.queued-turn.steer"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  expectedActiveTurnId: TurnId,
  createdAt: IsoDateTime,
});

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadUserInputRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.user-input.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  createdAt: IsoDateTime,
});

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

const ThreadCheckpointRevertCommand = Schema.Struct({
  type: Schema.Literal("thread.checkpoint.revert"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadSnoozeCommand,
  ThreadUnsnoozeCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ThreadTurnEnqueueCommand,
  ThreadQueuedTurnUpdateCommand,
  ThreadQueuedTurnDeleteCommand,
  ThreadQueuedTurnSteerCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadSessionStopCommand,
  ThreadCheckpointRevertCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadSnoozeCommand,
  ThreadUnsnoozeCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ClientThreadTurnEnqueueCommand,
  ClientThreadQueuedTurnUpdateCommand,
  ThreadQueuedTurnDeleteCommand,
  ThreadQueuedTurnSteerCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadSessionStopCommand,
  ThreadCheckpointRevertCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

export const OrchestrationCommand = DispatchableClientOrchestrationCommand;
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationThreadStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("tail"),
    window: OrchestrationThreadTimelineWindow,
  }),
  Schema.Struct({
    kind: Schema.Literal("delta"),
    delta: OrchestrationThreadTimelineDelta,
  }),
  Schema.Struct({
    kind: Schema.Literal("epoch-reset"),
    window: OrchestrationThreadTimelineWindow,
  }),
  Schema.Struct({
    kind: Schema.Literal("working-status"),
    epoch: OrchestrationTimelineEpoch,
    threadId: ThreadId,
    label: Schema.NullOr(Schema.String),
  }),
]);
export type OrchestrationThreadStreamItem = typeof OrchestrationThreadStreamItem.Type;

export const TurnCountRange = Schema.Struct({
  fromTurnCount: NonNegativeInt,
  toTurnCount: NonNegativeInt,
}).check(
  Schema.makeFilter(
    (input) =>
      input.fromTurnCount <= input.toTurnCount ||
      new SchemaIssue.InvalidValue(Option.some(input.fromTurnCount), {
        message: "fromTurnCount must be less than or equal to toTurnCount",
      }),
    { identifier: "OrchestrationTurnDiffRange" },
  ),
);

export const ThreadTurnDiff = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    diff: Schema.String,
  }),
  { unsafePreserveChecks: true },
);

export const DispatchResult = Schema.Struct({
  accepted: Schema.Literal(true),
});
export type DispatchResult = typeof DispatchResult.Type;

export const ProviderSessionImportAvailability = Schema.Literals([
  "available",
  "busy",
  "activity-unknown",
  "already-imported",
  "archived",
  "deleting",
]);
export type ProviderSessionImportAvailability = typeof ProviderSessionImportAvailability.Type;

export const OrchestrationListImportableProviderSessionsInput = Schema.Struct({
  projectId: ProjectId,
  providerInstanceId: ProviderInstanceId,
  cursor: Schema.optionalKey(TrimmedNonEmptyString),
  limit: Schema.optionalKey(PositiveInt.check(Schema.isLessThanOrEqualTo(100))),
});
export type OrchestrationListImportableProviderSessionsInput =
  typeof OrchestrationListImportableProviderSessionsInput.Type;

export const OrchestrationImportableProviderSession = Schema.Struct({
  candidateRef: TrimmedNonEmptyString,
  title: TrimmedNonEmptyString,
  lastActivityAt: IsoDateTime,
  availability: ProviderSessionImportAvailability,
  requiresActivityAcknowledgement: Schema.Boolean,
  existingThreadId: Schema.optionalKey(ThreadId),
});
export type OrchestrationImportableProviderSession =
  typeof OrchestrationImportableProviderSession.Type;

export const OrchestrationListImportableProviderSessionsResult = Schema.Struct({
  complete: Schema.optional(Schema.Boolean),
  items: Schema.Array(OrchestrationImportableProviderSession),
  cursor: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OrchestrationListImportableProviderSessionsResult =
  typeof OrchestrationListImportableProviderSessionsResult.Type;

export const OrchestrationImportProviderSessionInput = Schema.Struct({
  projectId: ProjectId,
  providerInstanceId: ProviderInstanceId,
  candidateRef: TrimmedNonEmptyString,
  requestedThreadId: ThreadId,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
  activityAcknowledgement: Schema.optionalKey(Schema.Literal("confirmed-stopped")),
});
export type OrchestrationImportProviderSessionInput =
  typeof OrchestrationImportProviderSessionInput.Type;

export const OrchestrationImportProviderSessionResult = Schema.Struct({
  threadId: ThreadId,
  outcome: Schema.Literals(["created", "existing", "unarchived"]),
});
export type OrchestrationImportProviderSessionResult =
  typeof OrchestrationImportProviderSessionResult.Type;

export class ProviderSessionImportError extends Schema.TaggedErrorClass<ProviderSessionImportError>()(
  "ProviderSessionImportError",
  {
    reason: Schema.Literals([
      "project-not-found",
      "provider-unavailable",
      "import-unsupported",
      "configuration-conflict",
      "candidate-invalid",
      "candidate-stale",
      "session-unavailable",
      "session-busy",
      "confirmation-required",
      "deleting-conflict",
      "history-unavailable",
      "persistence-failed",
    ]),
    message: TrimmedNonEmptyString,
  },
) {}

export const OrchestrationGetTurnDiffInput = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
  }),
  { unsafePreserveChecks: true },
);
export type OrchestrationGetTurnDiffInput = typeof OrchestrationGetTurnDiffInput.Type;

export const OrchestrationGetTurnDiffResult = ThreadTurnDiff;
export type OrchestrationGetTurnDiffResult = typeof OrchestrationGetTurnDiffResult.Type;

export const OrchestrationGetFullThreadDiffInput = Schema.Struct({
  threadId: ThreadId,
  toTurnCount: NonNegativeInt,
  ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationGetFullThreadDiffInput = typeof OrchestrationGetFullThreadDiffInput.Type;

export const OrchestrationGetFullThreadDiffResult = ThreadTurnDiff;
export type OrchestrationGetFullThreadDiffResult = typeof OrchestrationGetFullThreadDiffResult.Type;

export const OrchestrationRpcSchemas = {
  listImportableProviderProjects: {
    input: Schema.Struct({ providerInstanceId: ProviderInstanceId }),
    output: Schema.Struct({
      projects: Schema.Array(
        Schema.Struct({
          projectId: ProjectId,
          title: Schema.String,
          sessionCount: NonNegativeInt,
          unassigned: Schema.Boolean,
        }),
      ),
      complete: Schema.Literal(true),
    }),
  },
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  listImportableProviderSessions: {
    input: OrchestrationListImportableProviderSessionsInput,
    output: OrchestrationListImportableProviderSessionsResult,
  },
  importProviderSession: {
    input: OrchestrationImportProviderSessionInput,
    output: OrchestrationImportProviderSessionResult,
  },
  getArchivedShellSnapshot: {
    input: Schema.Struct({}),
    output: OrchestrationShellSnapshot,
  },
  getTurnDiff: {
    input: OrchestrationGetTurnDiffInput,
    output: OrchestrationGetTurnDiffResult,
  },
  getFullThreadDiff: {
    input: OrchestrationGetFullThreadDiffInput,
    output: OrchestrationGetFullThreadDiffResult,
  },
  getThreadTimelineBefore: {
    input: OrchestrationGetThreadTimelineBeforeInput,
    output: OrchestrationGetThreadTimelineBeforeResult,
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: OrchestrationThreadStreamItem,
  },
  subscribeShell: {
    input: OrchestrationSubscribeShellInput,
    output: OrchestrationShellStreamItem,
  },
} as const;

export class OrchestrationGetSnapshotError extends Schema.TaggedErrorClass<OrchestrationGetSnapshotError>()(
  "OrchestrationGetSnapshotError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const OrchestrationDispatchDisposition = Schema.Literals([
  "send-failed",
  "send-outcome-unknown",
  "history-unavailable",
  "provider-unavailable",
  "checkpoint-unavailable",
]);
export type OrchestrationDispatchDisposition = typeof OrchestrationDispatchDisposition.Type;

export const OrchestrationDispatchErrorCode = Schema.Literals(["unsupported-image-mime-type"]);
export type OrchestrationDispatchErrorCode = typeof OrchestrationDispatchErrorCode.Type;

export class OrchestrationDispatchCommandError extends Schema.TaggedErrorClass<OrchestrationDispatchCommandError>()(
  "OrchestrationDispatchCommandError",
  {
    message: TrimmedNonEmptyString,
    disposition: Schema.optional(OrchestrationDispatchDisposition),
    code: Schema.optional(OrchestrationDispatchErrorCode),
    retainedThread: Schema.optional(Schema.Literal(true)),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const OrchestrationCheckpointFailureReason = Schema.Literals([
  "thread-not-found",
  "workspace-missing",
  "unsupported",
  "history-unavailable",
  "history-inconsistent",
  "range-unavailable",
  "ref-unavailable",
  "store-failure",
]);
export type OrchestrationCheckpointFailureReason = typeof OrchestrationCheckpointFailureReason.Type;

export class OrchestrationGetTurnDiffError extends Schema.TaggedErrorClass<OrchestrationGetTurnDiffError>()(
  "OrchestrationGetTurnDiffError",
  {
    message: TrimmedNonEmptyString,
    reason: OrchestrationCheckpointFailureReason,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationGetFullThreadDiffError extends Schema.TaggedErrorClass<OrchestrationGetFullThreadDiffError>()(
  "OrchestrationGetFullThreadDiffError",
  {
    message: TrimmedNonEmptyString,
    reason: OrchestrationCheckpointFailureReason,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
